import { useState, useEffect } from 'react';
import { X, Search, Zap, Bus, DollarSign, CreditCard, Smartphone, Pencil } from 'lucide-react';
import { stockApi, ventasApi, busesApi } from '../lib/api';
import { topeVenta } from '../lib/autorizacionKit';
import { coincideProducto } from '../lib/buscar';
import { toast } from 'sonner';
import CambiarPrecioModal from './CambiarPrecioModal';
import TicketImprimible from './TicketImprimible';
import BusSelect from './BusSelect';

interface VentaRapidaModalProps {
  mantenimientoId?: string;
  placaBus?: string;
  onClose: () => void;
  onCompletada?: (venta: any) => void;
}

const VentaRapidaModal = ({ mantenimientoId, placaBus, onClose, onCompletada }: VentaRapidaModalProps) => {
  // 2026-10-22: precio cambiado por el cajero (con clave si el producto tiene precio)
  const [precioManual, setPrecioManual] = useState<number | null>(null);
  const [editandoPrecio, setEditandoPrecio] = useState(false);
  const [autorizacionPrecio, setAutorizacionPrecio] = useState<{ clave: string; motivo: string } | null>(null);
  const [productos, setProductos] = useState<any[]>([]);
  const [busqueda, setBusqueda] = useState('');
  const [productoSeleccionado, setProductoSeleccionado] = useState<any>(null);
  // Cantidad como texto (no numero): un input controlado con value=numero pierde el
  // "." apenas se escribe (parseFloat("2.") es 2), lo que hace imposible tipear
  // decimales para productos por metro/cm. Se parsea a numero solo donde se necesita.
  const [cantidad, setCantidad] = useState('1');
  const cantidadNum = parseFloat(cantidad) || 0;
  // Para productos "metro": permite tipear la cantidad a vender en centimetros. El
  // valor real usado para stock/precio/payload siempre se convierte a metros.
  const [cantidadEnCm, setCantidadEnCm] = useState(false);
  const cantidadRealNum = productoSeleccionado?.unidad === 'metro' && cantidadEnCm ? cantidadNum / 100 : cantidadNum;
  // 2026-10-22: precio en uso (el de lista o el cambiado); al cambiar de producto vuelve al de lista
  const precioUsado = precioManual ?? Number(productoSeleccionado?.precio_venta || productoSeleccionado?.precio || 0);
  useEffect(() => { setPrecioManual(null); setAutorizacionPrecio(null); }, [productoSeleccionado?.id]);
  const [procesando, setProcesando] = useState(false);
  const [ventaParaImprimir, setVentaParaImprimir] = useState<any>(null);
  const [buses, setBuses] = useState<any[]>([]);
  const [placaBusElegida, setPlacaBusElegida] = useState(placaBus || '');
  const [tipoPago, setTipoPago] = useState<'efectivo' | 'credito' | 'mixto' | 'yape'>('efectivo');
  // A nombre de quien sale el ticket cuando no hay padron (2026-10-09, pedido de Milenko)
  const [aNombreDe, setANombreDe] = useState('');
  const [montoEfectivo, setMontoEfectivo] = useState('');
  const [montoCredito, setMontoCredito] = useState('');
  const [capturasYape, setCapturasYape] = useState<string[]>([]);
  const [mostrarModalCapturaYape, setMostrarModalCapturaYape] = useState(false);

  useEffect(() => {
    stockApi.getFull().then((data) => setProductos((data || []).filter((p: any) => p.estado === 'activo'))).catch(() => {});
    // Se carga siempre (no solo cuando falta el padron): hace falta para mostrar la
    // deuda del bus si se elige pagar a credito.
    busesApi.getAll().then((data) => setBuses(data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (ventaParaImprimir) {
      const timer = setTimeout(() => {
        window.print();
        onCompletada?.(ventaParaImprimir);
        onClose();
      }, 300);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ventaParaImprimir]);

  const productosFiltrados = busqueda.trim()
    ? productos.filter(p => coincideProducto(busqueda, p))
    : productos;

  // Capturas de pago de Yape: se leen como base64 y se mandan asi al backend,
  // que las guarda como archivos y las liga a la venta recien creada.
  const agregarCapturasYape = (files: FileList | null) => {
    if (!files) return;
    Array.from(files).forEach(file => {
      const lector = new FileReader();
      lector.onload = () => setCapturasYape(prev => [...prev, lector.result as string]);
      lector.readAsDataURL(file);
    });
  };

  const quitarCapturaYape = (index: number) => {
    setCapturasYape(prev => prev.filter((_, i) => i !== index));
  };

  const venderEImprimir = async () => {
    // El padron solo es obligatorio si queda deuda (credito o mixto): la deuda va a nombre
    // del bus. Al contado (efectivo o Yape) se puede vender sin padron (2026-10-09).
    if (!placaBusElegida.trim() && (tipoPago === 'credito' || tipoPago === 'mixto')) {
      toast.error('Para vender a crédito selecciona el padrón del bus');
      return;
    }
    if (!productoSeleccionado) {
      toast.error('Selecciona un producto');
      return;
    }
    if (cantidadRealNum <= 0 || cantidadRealNum > topeVenta(productoSeleccionado)) {
      toast.error(`Cantidad inválida (stock disponible: ${productoSeleccionado.stock})`);
      return;
    }

    const precio = precioUsado;
    const subtotal = precio * cantidadRealNum;
    const igv = 0;
    const total = subtotal;

    if (tipoPago === 'mixto') {
      const efectivo = parseFloat(montoEfectivo) || 0;
      const credito = parseFloat(montoCredito) || 0;
      if (efectivo <= 0 || credito <= 0) {
        toast.error('Ambos montos deben ser mayores a cero en venta mixta');
        return;
      }
      if (Math.abs((efectivo + credito) - total) > 0.01) {
        toast.error(`La suma de efectivo (S/ ${efectivo.toFixed(2)}) y crédito (S/ ${credito.toFixed(2)}) debe ser igual al total (S/ ${total.toFixed(2)})`);
        return;
      }
    }

    // La captura de Yape es opcional: si no se adjunta ahora, se puede subir
    // despues desde el Historial de Ventas.

    setProcesando(true);
    try {
      const venta = await ventasApi.crearConCarrito({
        cliente_nombre: (!placaBusElegida.trim() && aNombreDe.trim()) ? aNombreDe.trim().slice(0, 80) : 'Cliente Contado',
        placa_bus: placaBusElegida.trim() || null,
        tipo_pago: tipoPago,
        items: [{ id: productoSeleccionado.id, nombre: productoSeleccionado.nombre, cantidad: cantidadRealNum, precio }],
        autorizacion_precio: autorizacionPrecio || undefined,
        subtotal,
        igv,
        total,
        monto_credito: tipoPago === 'mixto' ? parseFloat(montoCredito) : undefined,
        yape_capturas: tipoPago === 'yape' ? capturasYape : undefined,
        mantenimiento_id: mantenimientoId || undefined,
        cerrada: true, // Venta Rapida siempre imprime al toque
        observaciones: `${mantenimientoId ? 'Venta rápida - repuesto usado en mantenimiento' : 'Venta rápida'}${tipoPago === 'mixto' ? ` - MIXTO: Efectivo S/${montoEfectivo} + Crédito S/${montoCredito}` : ''}`
      });
      toast.success(`Venta ${venta.numero} registrada`);
      setVentaParaImprimir(venta);
    } catch (error: any) {
      console.error('Error en venta rápida:', error);
      toast.error('Error al procesar la venta: ' + error.message);
      setProcesando(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4"
    >
      <div className="bg-white rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b flex justify-between items-center">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Zap className="text-yellow-500" size={20} />
            Venta Rápida
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {mantenimientoId && (
            <p className="text-sm bg-blue-50 border border-blue-200 text-blue-700 rounded-lg px-3 py-2">
              Este repuesto quedará ligado al mantenimiento en curso{placaBus ? ` del bus ${placaBus}` : ''}.
            </p>
          )}

          {!placaBus && (
            <div>
              <label className="block text-sm font-medium mb-1 flex items-center gap-1">
                <Bus size={16} className="text-gray-500" />
                Bus / Padrón {tipoPago === 'credito' || tipoPago === 'mixto' ? '*' : <span className="text-gray-400 font-normal">(opcional al contado)</span>}
              </label>
              <BusSelect buses={buses} value={placaBusElegida} onChange={(v) => setPlacaBusElegida(v)} className="w-full border rounded-lg px-3 py-2" />
              {!placaBusElegida && (
                <input
                  type="text"
                  value={aNombreDe}
                  onChange={(e) => setANombreDe(e.target.value)}
                  maxLength={80}
                  placeholder="Sin padrón: a nombre de (opcional), ej. Particular"
                  className="w-full border rounded-lg px-3 py-2 mt-2 text-sm"
                />
              )}
            </div>
          )}

          {!productoSeleccionado ? (
            <>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <input
                  type="text"
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  placeholder="Buscar producto por nombre o código..."
                  className="w-full pl-9 pr-3 py-2 border rounded-lg"
                  autoFocus
                />
              </div>
              <div className="max-h-72 overflow-y-auto space-y-2">
                {productosFiltrados.slice(0, 100).map((p) => (
                  <button
                    key={p.id}
                    onClick={() => { setProductoSeleccionado(p); setCantidad('1'); setCantidadEnCm(false); }}
                    disabled={topeVenta(p) <= 0}
                    className="w-full text-left border rounded-lg p-3 hover:bg-gray-50 disabled:opacity-40 flex justify-between items-center"
                  >
                    <div>
                      <p className="font-medium">{p.nombre}{p.es_kit && <span className="ml-2 px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 text-xs font-semibold">KIT</span>}</p>
                      <p className="text-xs text-gray-500">{p.es_kit ? 'Kits armables' : 'Stock'}: {p.stock}</p>
                    </div>
                    <p className="font-bold text-green-600">S/ {(p.precio_venta || p.precio || 0).toFixed(2)}</p>
                  </button>
                ))}
                {productosFiltrados.length === 0 && (
                  <p className="text-center text-gray-500 py-6">Sin resultados</p>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="border rounded-lg p-4 bg-gray-50">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-semibold">{productoSeleccionado.nombre}</p>
                    <p className="text-sm text-gray-500">{productoSeleccionado.es_kit ? 'Kits armables' : 'Stock disponible'}: {productoSeleccionado.stock}</p>
                  </div>
                  <button onClick={() => { setProductoSeleccionado(null); setCantidadEnCm(false); }} className="text-sm text-blue-600 hover:underline">
                    Cambiar
                  </button>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium">
                    Cantidad {productoSeleccionado.unidad === 'metro' ? `(${cantidadEnCm ? 'cm' : 'metros'})` : (productoSeleccionado.unidad && productoSeleccionado.unidad !== 'unidad' ? `(${productoSeleccionado.unidad})` : '')}
                  </label>
                  {productoSeleccionado.unidad === 'metro' && (
                    <button
                      type="button"
                      onClick={() => { setCantidadEnCm(v => !v); setCantidad('1'); }}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      Ingresar en {cantidadEnCm ? 'metros' : 'cm'}
                    </button>
                  )}
                </div>
                <input
                  type="number"
                  step="0.01"
                  min={0.01}
                  max={productoSeleccionado.unidad === 'metro' && cantidadEnCm ? productoSeleccionado.stock * 100 : topeVenta(productoSeleccionado)}
                  value={cantidad}
                  onChange={(e) => setCantidad(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Tipo de Pago</label>
                <div className="grid grid-cols-4 gap-2">
                  <button
                    type="button"
                    onClick={() => { setTipoPago('efectivo'); setMontoEfectivo(''); setMontoCredito(''); setCapturasYape([]); }}
                    className={`p-2 rounded-lg border-2 flex flex-col items-center justify-center text-xs ${tipoPago === 'efectivo' ? 'border-blue-600 bg-blue-50' : 'border-gray-300'}`}
                  >
                    <DollarSign className="w-4 h-4 mb-1" />
                    Contado
                  </button>
                  <button
                    type="button"
                    onClick={() => { setTipoPago('credito'); setMontoEfectivo(''); setMontoCredito(''); setCapturasYape([]); }}
                    className={`p-2 rounded-lg border-2 flex flex-col items-center justify-center text-xs ${tipoPago === 'credito' ? 'border-blue-600 bg-blue-50' : 'border-gray-300'}`}
                  >
                    <CreditCard className="w-4 h-4 mb-1" />
                    Crédito
                  </button>
                  <button
                    type="button"
                    onClick={() => { setTipoPago('mixto'); setMontoEfectivo(''); setMontoCredito(''); setCapturasYape([]); }}
                    className={`p-2 rounded-lg border-2 flex flex-col items-center justify-center text-xs ${tipoPago === 'mixto' ? 'border-blue-600 bg-blue-50' : 'border-gray-300'}`}
                  >
                    <div className="flex mb-1"><DollarSign className="w-3 h-3" /><CreditCard className="w-3 h-3" /></div>
                    Mixto
                  </button>
                  <button
                    type="button"
                    onClick={() => { setTipoPago('yape'); setMontoEfectivo(''); setMontoCredito(''); }}
                    className={`p-2 rounded-lg border-2 flex flex-col items-center justify-center text-xs ${tipoPago === 'yape' ? 'border-purple-600 bg-purple-50' : 'border-gray-300'}`}
                  >
                    <Smartphone className="w-4 h-4 mb-1" />
                    Yape
                  </button>
                </div>
              </div>

              {(tipoPago === 'credito' || tipoPago === 'mixto') && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-sm text-blue-800">
                  {placaBusElegida ? (
                    <>El crédito queda a nombre del bus <strong>{placaBusElegida}</strong>. Deuda actual: S/ {(buses.find(b => b.padron === placaBusElegida)?.credito_usado || 0).toFixed(2)}</>
                  ) : (
                    'Seleccioná el bus/padrón: el crédito se registra a su nombre, no al de una persona.'
                  )}
                </div>
              )}

              {tipoPago === 'mixto' && (
                <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200 space-y-2">
                  <div>
                    <label className="block text-xs font-medium mb-1">Monto Efectivo (S/)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={montoEfectivo}
                      onChange={(e) => {
                        const totalVenta = precioUsado * cantidadRealNum;
                        const efectivo = parseFloat(e.target.value) || 0;
                        setMontoEfectivo(e.target.value);
                        setMontoCredito(Math.max(0, totalVenta - efectivo).toFixed(2));
                      }}
                      className="w-full border rounded-lg px-3 py-2 text-sm"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Monto a Crédito (S/)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={montoCredito}
                      onChange={(e) => {
                        const totalVenta = precioUsado * cantidadRealNum;
                        const credito = parseFloat(e.target.value) || 0;
                        setMontoCredito(e.target.value);
                        setMontoEfectivo(Math.max(0, totalVenta - credito).toFixed(2));
                      }}
                      className="w-full border rounded-lg px-3 py-2 text-sm"
                      placeholder="0.00"
                    />
                  </div>
                </div>
              )}

              {tipoPago === 'yape' && (
                <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-semibold text-purple-800">Comprobante de Yape</h4>
                    <button
                      type="button"
                      onClick={() => setMostrarModalCapturaYape(true)}
                      className="text-xs px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                    >
                      + Agregar captura
                    </button>
                  </div>
                  {capturasYape.length === 0 ? (
                    <p className="text-xs text-purple-700">Sin capturas todavía. Es opcional, se puede subir después desde el Historial.</p>
                  ) : (
                    <div className="grid grid-cols-4 gap-2">
                      {capturasYape.map((captura, i) => (
                        <div key={i} className="relative">
                          <img src={captura} alt={`Captura ${i + 1}`} className="w-full h-16 object-cover rounded border" />
                          <button
                            type="button"
                            onClick={() => quitarCapturaYape(i)}
                            className="absolute -top-1.5 -right-1.5 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="border-t pt-3 flex justify-between font-bold text-lg">
                <span>Total:</span>
                <span>S/ {(precioUsado * cantidadRealNum).toFixed(2)}</span>
              </div>
              <button type="button" onClick={() => setEditandoPrecio(true)}
                      className="text-xs text-blue-700 hover:underline flex items-center gap-1">
                <Pencil className="w-3 h-3" /> Precio: S/ {precioUsado.toFixed(2)} c/u{precioManual !== null ? ' (cambiado)' : ''} — cambiar
              </button>
              {editandoPrecio && (
                <CambiarPrecioModal
                  nombre={productoSeleccionado.nombre}
                  precioLista={Number(productoSeleccionado.precio_venta || productoSeleccionado.precio || 0)}
                  precioActual={precioUsado}
                  onClose={() => setEditandoPrecio(false)}
                  onAplicar={(p, aut) => { setPrecioManual(p); if (aut) setAutorizacionPrecio(aut); setEditandoPrecio(false); }}
                />
              )}

              <button
                onClick={venderEImprimir}
                disabled={procesando}
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-white font-semibold py-3 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <Zap size={18} />
                {procesando ? 'Procesando...' : 'Vender e Imprimir'}
              </button>
            </>
          )}
        </div>
      </div>

      {ventaParaImprimir && <TicketImprimible venta={ventaParaImprimir} />}

      {mostrarModalCapturaYape && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[80]"
        >
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Smartphone className="w-5 h-5 text-purple-600" />
                Capturas de pago Yape
              </h3>
              <button onClick={() => setMostrarModalCapturaYape(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <label className="flex flex-col items-center justify-center border-2 border-dashed border-purple-300 rounded-lg py-8 cursor-pointer hover:bg-purple-50">
              <Smartphone className="w-8 h-8 text-purple-400 mb-2" />
              <span className="text-sm text-purple-700 font-medium">Click para elegir captura(s) de pantalla</span>
              <span className="text-xs text-gray-500 mt-1">Podés seleccionar varias a la vez</span>
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => { agregarCapturasYape(e.target.files); e.target.value = ''; }}
              />
            </label>

            {capturasYape.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mt-4">
                {capturasYape.map((captura, i) => (
                  <div key={i} className="relative">
                    <img src={captura} alt={`Captura ${i + 1}`} className="w-full h-24 object-cover rounded border" />
                    <button
                      type="button"
                      onClick={() => quitarCapturaYape(i)}
                      className="absolute -top-1.5 -right-1.5 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <button
              onClick={() => setMostrarModalCapturaYape(false)}
              className="w-full mt-6 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              Listo ({capturasYape.length} captura{capturasYape.length === 1 ? '' : 's'})
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VentaRapidaModal;
